import { Injectable } from '@angular/core';

import { PowerBiService } from '@core/services';
import { PowerBIQueryRow } from '@core/models';
import { ConfigurationService } from '@common/services';

import { SalesHighlight } from '@bpm/models';

/** Período (año y mes) sobre el que se calculan los destacados */
export interface SalesPeriod {
    year: number;
    month: number;
}

/**
 * Contenido del campo `data` de la configuración de common.
 *
 * Las queries viven ahí y no en el código: son DAX contra el modelo semántico,
 * y cambian cuando cambia el modelo (una medida nueva, otro filtro de cluster)
 * sin que eso tenga nada que ver con una versión del front.
 */
export interface SalesHighlightConfig {
    /** Dataset (modelo semántico) contra el que corren las queries */
    datasetId: string;
    /** Workspace del dataset; 'me' o vacío para el del propio usuario */
    groupId?: string;
    /** DAX del top de marcas */
    brandsQuery: string;
    /** DAX del top de países */
    countriesQuery: string;
}

/** Nombre con el que se busca la configuración en common */
const CONFIG_NAME = 'Sales Highlights';

@Injectable({ providedIn: 'root' })
export class SalesHighlightService {

    /**
     * Configuración en curso.
     *
     * Se cachea la promesa y no el resultado: las dos consultas salen en
     * paralelo, y así la configuración se pide una sola vez.
     */
    private _config: Promise<SalesHighlightConfig | null> | null = null;

    /**
     * Constructor
     * @param _powerBiService
     * @param _configurationService
     */
    constructor(
        private _powerBiService: PowerBiService,
        private _configurationService: ConfigurationService
    ) { }

    /**
     * Último mes cerrado, que es el que tiene datos cargados en el modelo.
     *
     * El mes en curso todavía se está cargando, así que los destacados miran
     * siempre el anterior. En enero eso es diciembre del año pasado.
     *
     * @returns {SalesPeriod} Año y mes (1-12) del último cierre
     */
    closedPeriod(): SalesPeriod {
        const today = new Date();
        const previous = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return { year: previous.getFullYear(), month: previous.getMonth() + 1 };
    }

    /**
     * Marcas a destacar del mes, ordenadas por impacto contra el AOP.
     *
     * El ranking lo arma el DAX: promedia la posición por impacto en venta y
     * por impacto en margen, y pone primero a las que están arriba del AOP en
     * las dos cosas.
     *
     * @param {SalesPeriod} period Período a consultar; por defecto el último cierre
     * @param {number} top Cuántas marcas devolver
     * @returns {Promise<SalesHighlight[]>} Marcas destacadas
     */
    async getTopBrands(period?: SalesPeriod, top: number = 3): Promise<SalesHighlight[]> {
        const config = await this._loadConfig();
        return this._runQuery(config, config?.brandsQuery, period, top);
    }

    /**
     * Países a destacar del mes, con el mismo criterio que las marcas.
     *
     * @param {SalesPeriod} period Período a consultar; por defecto el último cierre
     * @param {number} top Cuántos países devolver
     * @returns {Promise<SalesHighlight[]>} Países destacados
     */
    async getTopCountries(period?: SalesPeriod, top: number = 3): Promise<SalesHighlight[]> {
        const config = await this._loadConfig();
        return this._runQuery(config, config?.countriesQuery, period, top);
    }

    /**
     * Trae la configuración de common, una sola vez por sesión.
     *
     * Si falla se limpia la caché, así un reintento posterior vuelve a pedirla
     * en lugar de arrastrar el error toda la sesión.
     *
     * @returns {Promise<SalesHighlightConfig|null>} Configuración de los destacados
     */
    private _loadConfig(): Promise<SalesHighlightConfig | null> {
        if (!this._config) {
            this._config = this._configurationService.getByName(CONFIG_NAME)
                .toPromise()
                .then(resp => this._parseConfig(resp?.data))
                .catch(error => {
                    this._config = null;
                    throw error;
                });
        }

        return this._config;
    }

    /** El backend puede devolver `data` como objeto o como json en texto */
    private _parseConfig(data: any): SalesHighlightConfig | null {
        if (!data) return null;
        if (typeof data !== 'string') return data as SalesHighlightConfig;

        try {
            return JSON.parse(data) as SalesHighlightConfig;
        } catch {
            return null;
        }
    }

    /**
     * Corre una de las queries de la configuración y arma el modelo.
     *
     * Sin configuración cargada devuelve vacío en lugar de romper: el bloque
     * de destacados se esconde solo cuando no hay nada que mostrar.
     *
     * @param config Configuración de common
     * @param query DAX a ejecutar, con los marcadores de período sin resolver
     * @param period Período a consultar; por defecto el último cierre
     * @param top Cuántas filas conservar
     */
    private async _runQuery(
        config: SalesHighlightConfig | null, query: string | undefined,
        period: SalesPeriod | undefined, top: number
    ): Promise<SalesHighlight[]> {

        if (!config?.datasetId || !query) return [];

        const dax = this._applyPeriod(query, period || this.closedPeriod());
        const rows = await this._powerBiService.executeQuery(config.datasetId, dax, config.groupId);

        return this._toHighlights(rows, top);
    }

    /**
     * Reemplaza los marcadores de período en la query guardada.
     *
     * En la configuración el DAX se escribe con `{{year}}` y `{{month}}` donde
     * van el año y el mes, para no tener que guardar una query por mes.
     *
     * @param query DAX tal cual está en la configuración
     * @param period Período a resolver
     */
    private _applyPeriod(query: string, period: SalesPeriod): string {
        return (query || '')
            .replace(/\{\{\s*year\s*\}\}/gi, String(period.year))
            .replace(/\{\{\s*month\s*\}\}/gi, String(period.month));
    }

    /**
     * Pasa las filas crudas al modelo y se queda con las primeras `top`.
     *
     * El orden ya viene resuelto por el ORDER BY de la query, así que acá no
     * se reordena nada.
     *
     * @param rows Filas devueltas por executeQueries
     * @param top Cuántas filas conservar
     */
    private _toHighlights(rows: PowerBIQueryRow[], top: number): SalesHighlight[] {
        return (rows || []).slice(0, top).map(row => {
            const highlight = new SalesHighlight();
            highlight.name = this._text(row, this._nameColumn(row));
            highlight.actualVolume = this._number(row, 'VolumenReal');
            highlight.aopVolume = this._number(row, 'VolumenAOP');
            highlight.lastYearVolume = this._number(row, 'VolumenYAG');
            highlight.actualMargin = this._number(row, 'MargenReal');
            highlight.aopMargin = this._number(row, 'MargenAOP');
            highlight.volumeImpact = this._number(row, 'ImpactoVolumen');
            highlight.marginImpact = this._number(row, 'ImpactoMargen');
            highlight.bothPositive = this._number(row, 'AmbosPositivos') === 1;
            return highlight;
        });
    }

    /**
     * Columna que trae el nombre de la marca o del país.
     *
     * No hace falta configurarla: las columnas calculadas con ADDCOLUMNS
     * llegan entre corchetes sueltos ('[VentaReal]') y la que agrupa conserva
     * la tabla adelante ('c_sku[brand]'), así que es la primera que no empieza
     * con corchete. Vale para las dos queries y para cualquiera que se agregue.
     */
    private _nameColumn(row: PowerBIQueryRow): string {
        return Object.keys(row || {}).find(key => !key.startsWith('[')) || '';
    }

    /**
     * Lee una columna calculada de la fila.
     *
     * El API nombra las columnas de ADDCOLUMNS entre corchetes ('[VentaReal]'),
     * pero según el modelo puede devolverlas con la tabla adelante o sin los
     * corchetes: se prueban las tres formas antes de darla por vacía.
     */
    private _value(row: PowerBIQueryRow, column: string): any {
        if (row == null || !column) return null;
        if (row[`[${column}]`] !== undefined) return row[`[${column}]`];
        if (row[column] !== undefined) return row[column];

        const key = Object.keys(row).find(k => k.endsWith(`[${column}]`));
        return key ? row[key] : null;
    }

    private _number(row: PowerBIQueryRow, column: string): number {
        const value = this._value(row, column);
        return typeof value === 'number' ? value : Number(value) || 0;
    }

    private _text(row: PowerBIQueryRow, column: string): string {
        const value = this._value(row, column);
        return value == null ? '' : String(value);
    }
}
